import type { ImmerStateCreator } from 'hooks/useStore';
import type {
  TicketLayoutField,
  TicketLayoutWithFields,
} from 'containers/Ticketing/_api/useGetTicketLayoutDetails/types';
import type { IroncladWorkflowConfig } from 'pages/app/users/integrations/utils/hooks/ironclad/types';
import type { TicketLayoutsCount, TicketLayoutView } from '../types';
import { IntegrationSource } from '../types';

export interface TicketLayoutSlice {
  selectedTicketLayoutId: string | null;
  selectedTicketFieldId: string | null;
  selectedTicketLayoutTab: TicketLayoutView | null;
  count: TicketLayoutsCount;
  showTicketLayoutModal: boolean;
  refreshTicketLayouts: boolean;
  validationError: Record<string, string>;
  ticketLayout: TicketLayoutWithFields;
  usedTicketFields: TicketLayoutField[];
  unusedTicketFields: TicketLayoutField[];
  integrationSource: IntegrationSource;
  ironcladWorkflowConfigId?: string;
  ironcladWorkflowConfig?: IroncladWorkflowConfig;
  setValidationErrors: (errors: Record<string, string>) => void;
  setSelectedTicketLayoutId: (id: string | null) => void;
  setSelectedTicketFieldId: (id: string | null) => void;
  setSelectedTicketLayoutTab: (tab: TicketLayoutView | null) => void;
  setCount: (count: TicketLayoutsCount) => void;
  setShowTicketLayoutModal: (show: boolean) => void;
  setRefreshTicketLayouts: (refresh: boolean) => void;
  setTicketLayout: (ticketLayout: TicketLayoutWithFields) => void;
  setUsedTicketFields: (fields: TicketLayoutField[]) => void;
  setUnusedTicketFields: (fields: TicketLayoutField[]) => void;
  setUsedTicketFieldsFromPrevious: (
    factory: (prev: TicketLayoutField[]) => TicketLayoutField[]
  ) => void;
  setIntegrationSource: (source: IntegrationSource) => void;
  setIroncladWorkflowConfigId: (id: string | undefined) => void;
  setIroncladWorkflowConfig: (
    ironcladWorkflowConfig: IroncladWorkflowConfig | undefined
  ) => void;
}

export const useTicketLayoutSlice: ImmerStateCreator<TicketLayoutSlice> = (
  set
) => ({
  selectedTicketLayoutId: null,
  selectedTicketFieldId: null,
  selectedTicketLayoutTab: null,
  count: {
    live: 0,
    archived: 0,
    total: 0,
  },
  showTicketLayoutModal: false,
  refreshTicketLayouts: false,
  validationError: {
    numMin: '',
    numMax: '',
    numDefaultValue: '',
  },
  ticketLayout: {
    name: '',
    description: null,
    fields: [],
  },
  usedTicketFields: [],
  unusedTicketFields: [],
  integrationSource: IntegrationSource.CHECKBOX,
  ironcladWorkflowConfig: undefined,
  setValidationErrors: (errors: Record<string, string>) =>
    set(
      (state) => {
        state.ticketLayoutSlice.validationError = errors;
      },
      false,
      'setValidationErrors'
    ),
  setSelectedTicketLayoutTab: (tab: TicketLayoutView | null) =>
    set(
      (state) => {
        state.ticketLayoutSlice.selectedTicketLayoutTab = tab;
      },
      false,
      'setSelectedTicketLayoutTab'
    ),
  setCount: (count: TicketLayoutsCount) =>
    set(
      (state) => {
        state.ticketLayoutSlice.count = count;
      },
      false,
      'setCount'
    ),
  setSelectedTicketLayoutId: (id: string | null) =>
    set(
      (state) => {
        state.ticketLayoutSlice.selectedTicketLayoutId = id;
      },
      false,
      'setSelectTicketLayoutId'
    ),
  setSelectedTicketFieldId: (id: string | null) =>
    set(
      (state) => {
        state.ticketLayoutSlice.selectedTicketFieldId = id;
      },
      false,
      'setSelectedTicketFieldId'
    ),
  setShowTicketLayoutModal: (show: boolean) =>
    set(
      (state) => {
        state.ticketLayoutSlice.showTicketLayoutModal = show;
      },
      false,
      'setShowTicketLayoutModal'
    ),
  setRefreshTicketLayouts: (refresh: boolean) =>
    set(
      (state) => {
        state.ticketLayoutSlice.refreshTicketLayouts = refresh;
      },
      false,
      'setRefreshTicketLayouts'
    ),
  setTicketLayout: (ticketLayout: TicketLayoutWithFields) =>
    set(
      (state) => {
        state.ticketLayoutSlice.ticketLayout = ticketLayout;
      },
      false,
      'setTicketLayout'
    ),
  setUsedTicketFields: (usedTicketFields: TicketLayoutField[]) =>
    set(
      (state) => {
        state.ticketLayoutSlice.usedTicketFields = usedTicketFields;
      },
      false,
      'setUsedTicketFields'
    ),
  setUnusedTicketFields: (unusedTicketFields: TicketLayoutField[]) =>
    set(
      (state) => {
        state.ticketLayoutSlice.unusedTicketFields = unusedTicketFields;
      },
      false,
      'setUnusedTicketFields'
    ),
  setUsedTicketFieldsFromPrevious: (
    factory: (prev: TicketLayoutField[]) => TicketLayoutField[]
  ) => {
    set(
      (state) => {
        state.ticketLayoutSlice.usedTicketFields = factory(
          state.ticketLayoutSlice.usedTicketFields
        );
      },
      false,
      'setUsedTicketFieldsFromPrevious'
    );
  },
  setIntegrationSource: (source: IntegrationSource) =>
    set((state) => {
      state.ticketLayoutSlice.integrationSource = source;
    }),
  setIroncladWorkflowConfig: (config: IroncladWorkflowConfig | undefined) =>
    set((state) => {
      state.ticketLayoutSlice.ironcladWorkflowConfig = config;
    }),
  setIroncladWorkflowConfigId: (id: string | undefined) =>
    set((state) => {
      state.ticketLayoutSlice.ironcladWorkflowConfigId = id;
    }),
});
